import { Link } from "react-router-dom";
import { Phone, Star, Truck, Landmark } from "lucide-react";
import { BIZ, track, GOOGLE_RATING, GOOGLE_REVIEW_COUNT } from "../data/site";
import { Img } from "./Img";

const STRONG_SHADOW = "0 1px 2px rgba(0,0,0,0.65), 0 2px 10px rgba(0,0,0,0.55), 0 4px 28px rgba(0,0,0,0.45)";
const TEXT_SHADOW = "0 1px 2px rgba(0,0,0,0.7), 0 2px 12px rgba(0,0,0,0.5)";

const Divider = () => <span aria-hidden="true" className="h-3.5 w-px bg-white/40" />;

export const Hero = () => (
  <section
    id="top"
    data-testid="hero-section"
    className="relative overflow-hidden bg-[#4A5A6E] min-h-[100svh] lg:min-h-0 lg:h-[min(94vh,860px)] xl:h-[min(90vh,860px)]"
  >
    {/* Full-bleed background photo — bright, no global darkening */}
    <Img
      name="home-hero-blue-lamborghini"
      alt="Bright blue Lamborghini sports car parked on a sunny street"
      eager
      bleed
      className="absolute inset-0 h-full w-full object-cover object-[62%_40%] lg:object-[60%_62%]"
      sizes="100vw"
    />

    {/* One soft, edgeless radial shade behind the text area (desktop/tablet only) */}
    <div
      aria-hidden="true"
      className="pointer-events-none absolute inset-0 hidden md:block"
      style={{ background: "radial-gradient(ellipse 55% 70% at 22% 55%, rgba(5,11,24,0.55) 0%, rgba(5,11,24,0.30) 45%, rgba(5,11,24,0) 75%)" }}
    />
    {/* Mobile: vertical fade is the only shading — car stays bright in the top half */}
    <div
      aria-hidden="true"
      className="pointer-events-none absolute inset-0 md:hidden"
      style={{ background: "linear-gradient(180deg, rgba(5,11,24,0) 30%, rgba(5,11,24,0.55) 62%, rgba(5,11,24,0.85) 100%)" }}
    />
    {/* top strip keeps the header nav readable over the bright photo */}
    <div
      aria-hidden="true"
      className="pointer-events-none absolute inset-x-0 top-0 h-20 lg:h-28"
      style={{ background: "linear-gradient(180deg, rgba(5,11,24,0.45) 0%, rgba(5,11,24,0) 100%)" }}
    />
    {/* bottom edge blends the photo into the site's navy */}
    <div aria-hidden="true" className="pointer-events-none absolute inset-x-0 bottom-0 h-32 bg-gradient-to-b from-transparent to-[#050B18]" />

    {/* Text sits directly on the photo — no box, no panel */}
    <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 h-full flex flex-col justify-end lg:justify-center pt-24 pb-16 lg:py-0 xl:pt-16 xl:pb-4">
      <div className="w-full max-w-[540px] lg:max-w-[480px] xl:max-w-[540px]">
        <span
          className="inline-flex items-center gap-2.5 font-bold text-[13px] sm:text-sm tracking-[0.12em] uppercase text-white"
          style={{ textShadow: TEXT_SHADOW }}
          data-testid="hero-eyebrow"
        >
          <span className="h-px w-10 bg-accent" aria-hidden="true" />
          I-CAR Gold Class &bull; Carrollton, TX
        </span>

        <h1
          className="hero-h1 mt-4 font-display font-extrabold tracking-tight text-white text-balance"
          style={{ textShadow: STRONG_SHADOW }}
        >
          Certified Collision Repair &amp; Auto Body Shop in Carrollton, TX
        </h1>

        <p
          className="mt-4 max-w-xl text-lg sm:text-xl font-semibold leading-[1.5] text-white"
          style={{ textShadow: TEXT_SHADOW }}
          data-testid="hero-subheadline"
        >
          From daily drivers to Lamborghinis and Rolls-Royces — restored to factory condition, backed by a written lifetime warranty.
        </p>

        <div className="mt-7 flex flex-col sm:flex-row gap-4">
          <Link
            to="/free-estimate"
            data-testid="hero-free-estimate-cta"
            className="group inline-flex items-center justify-center gap-3 whitespace-nowrap rounded-full bg-accentdeep px-8 py-4 min-h-12 w-full sm:w-auto font-display font-bold text-sm sm:text-base tracking-wide text-white transition-colors duration-300 hover:bg-accenthover"
          >
            Get a Free Estimate
            <span aria-hidden="true">&rarr;</span>
          </Link>
          <a
            href={BIZ.phoneHref}
            data-testid="hero-call-now-cta"
            onClick={() => track("click_to_call", { location: "hero" })}
            className="inline-flex items-center justify-center gap-3 whitespace-nowrap rounded-full bg-white px-8 py-4 min-h-12 w-full sm:w-auto font-display font-bold text-sm sm:text-base tracking-wide text-ink transition-colors duration-300 hover:bg-accent hover:text-white"
          >
            <Phone size={17} strokeWidth={2.2} />
            Call {BIZ.phoneDisplay}
          </a>
        </div>

        <div className="mt-7 flex flex-wrap items-center gap-x-5 gap-y-3" style={{ textShadow: TEXT_SHADOW }} data-testid="hero-trust-anchors">
          <span data-testid="trust-anchor-rating" className="inline-flex items-center gap-2 text-sm font-bold text-white">
            <Star size={15} fill="currentColor" className="text-accent" aria-hidden="true" />
            {GOOGLE_RATING} &middot; {GOOGLE_REVIEW_COUNT} Google Reviews
          </span>
          <Divider />
          <span data-testid="trust-anchor-towing" className="inline-flex items-center gap-2 text-sm font-bold text-white">
            <Truck size={15} className="text-accent" strokeWidth={2} aria-hidden="true" />
            Free Towing
          </span>
          <Divider />
          <span data-testid="trust-anchor-insurance" className="inline-flex items-center gap-2 text-sm font-bold text-white">
            <Landmark size={15} className="text-accent" strokeWidth={2} aria-hidden="true" />
            All Major Insurance
          </span>
        </div>
      </div>
    </div>
  </section>
);
